import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import Swal from "sweetalert2";

import { api } from "../../lib/api";
import { useFormStore } from "../../store";
import type { ContactRow, DocumentRow, SubmissionPayload, SupplierFields } from "../../types";

import Step1Business from "./Step1Business";
import Step2Products from "./Step2Products";
import Step3Files from "./Step3Files";
import Step4Contacts from "./Step4Contacts";
import Step5Review from "./Step5Review";

interface SubmitResponse {
  sid: string;
  supplier_guid: string;
  mode: "new" | "update";
  message: string;
}

const STEPS = [
  { n: 1, label: "Business Info", icon: "fa-building" },
  { n: 2, label: "Products & Services", icon: "fa-box-open" },
  { n: 3, label: "Files", icon: "fa-file-lines" },
  { n: 4, label: "Contacts", icon: "fa-address-book" },
  { n: 5, label: "Review & Submit", icon: "fa-list-check" },
] as const;

const LAST_STEP = STEPS[STEPS.length - 1].n;

interface PrefillResponse {
  supplier: SupplierFields | null;
  contacts: ContactRow[];
  documents: DocumentRow[];
}

export default function SupplierForm() {
  const step = useFormStore((s) => s.step);
  const setStep = useFormStore((s) => s.setStep);
  const hydrate = useFormStore((s) => s.hydrate);
  const supplier = useFormStore((s) => s.supplier);
  // `originalSupplier.id` is the frozen prefill snapshot — using that
  // (instead of the editable `supplier.id`) keeps the heading stable across
  // the wizard session. MUST be called before any conditional return below
  // or React's hook ordering breaks.
  const originalSupplierId = useFormStore((s) => s.originalSupplier.id);
  const contacts = useFormStore((s) => s.contacts);
  const documents = useFormStore((s) => s.documents);
  const reset = useFormStore((s) => s.reset);
  const [submitting, setSubmitting] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(true);

  useEffect(() => {
    api
      .get<PrefillResponse>("/supplier/me")
      .then(({ data }) => {
        hydrate({
          supplier: data.supplier ?? null,
          contacts: data.contacts ?? [],
          documents: data.documents ?? [],
        });
        setPrefillLoading(false);
      })
      .catch((err: AxiosError) => {
        // On 401 the api interceptor fires `window.location.href = /sign-in`,
        // but the browser navigation isn't instant. Keep `prefillLoading`
        // TRUE so the loader stays visible during the redirect — otherwise
        // the wizard renders for a few hundred ms before the navigation
        // takes effect, which the user sees as a "flash of authenticated
        // content".
        if (err?.response?.status === 401) return;
        // Non-auth errors (503, network) fall through to an empty form so
        // the user can fill it out fresh.
        setPrefillLoading(false);
      });
  }, [hydrate]);

  if (prefillLoading) {
    return <PrefillLoader />;
  }

  const handleSubmit = async () => {
    // Strip rows the backend doesn't need to look at:
    //  - action="none": untouched prefill rows that hit no code path
    //  - action="delete" with id=null: deleted locally before save (never
    //    existed in Dataverse, no soft-delete needed)
    const payload: SubmissionPayload = {
      supplier,
      contacts: contacts.filter((c) => c.action !== "none" && !(c.action === "delete" && !c.id)),
      documents: documents.filter((d) => d.action !== "none" && !(d.action === "delete" && !d.id)),
    };

    // Guard: a supplier must have a primary contact unless it has no
    // active contacts at all. Auto-shift logic in Step 4 normally keeps
    // this satisfied, but a direct submit click could still hit this if
    // the user unset primary via the modal without picking a replacement.
    const activeContacts = payload.contacts.filter((c) => c.action !== "delete");
    if (activeContacts.length > 0 && !activeContacts.some((c) => c.is_primary)) {
      await Swal.fire({
        icon: "warning",
        title: "Primary contact required",
        text: "Please mark exactly one contact as the primary contact on Step 4 before submitting.",
        confirmButtonText: "Go to Step 4",
      });
      setStep(4);
      return;
    }

    setSubmitting(true);
    Swal.fire({
      title: "Updating your business profile…",
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
    try {
      const { data } = await api.post<SubmitResponse>("/submit", payload);
      if (data.mode === "new") {
        // New-supplier registration sessions are anonymous (no SAML); the
        // session cookie still carries `supplier_id=""` after submit, so
        // re-fetching /supplier/me would return an empty payload and wipe
        // the just-submitted data from view. Instead, show a "registration
        // received" modal and on dismiss redirect home so the user lands on
        // a coherent post-submission state rather than back at an empty
        // wizard.
        await Swal.fire({
          icon: "success",
          title: "Registration received",
          text: (
            `Thank you for registering as a supplier (${data.sid}). ` +
            "Supplier Relations will review your submission and reach out " +
            "shortly to set up your portal sign-in so you can manage your " +
            "listing going forward."
          ),
          confirmButtonText: "Done",
        });
        reset();
        window.location.href = "/sign-in";
        return;
      } else {
        // Update flow: refetch so the live state becomes the new
        // originalSupplier snapshot — clears "pending changes" in Step 5.
        const refreshed = await api.get<PrefillResponse>("/supplier/me");
        hydrate({
          supplier: refreshed.data.supplier ?? null,
          contacts: refreshed.data.contacts ?? [],
          documents: refreshed.data.documents ?? [],
        });
        await Swal.fire({
          icon: "success",
          title: "Business profile updated",
          text: data.message,
          confirmButtonText: "Done",
        });
        setStep(1);
      }
    } catch (err) {
      const detail = extractErrorDetail(err);
      await Swal.fire({
        icon: "error",
        title: "Submission failed",
        text: detail,
        confirmButtonText: "Close",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isNewSupplier = originalSupplierId == null;
  const companyName = supplier.new_name?.trim();
  // Existing supplier may already have a logo on file (legacy hosting or a
  // prior upload). Show it inline with the title for visual context.
  const companyLogoUrl = !isNewSupplier ? supplier.new_logourl : undefined;

  const handleViewProfile = () => {
    // Placeholder — the public Franchise-Member view of a supplier listing
    // is a separate page that's not built yet. Stubbed for now so the entry
    // point exists; wire to the real route when that page lands.
    Swal.fire({
      icon: "info",
      title: "Coming soon",
      text: "The public supplier profile view will live here. For now you can preview your listing on the alliancefranchisebrands.com supplier directory.",
      confirmButtonText: "OK",
    });
  };

  return (
    <main className="container py-3">
      {/*
        Logo sits on the body's CDN background image — no card chrome here.
        Matches the AFB Hub header layout so the portal feels like part of
        the same family of internal apps.
      */}
      <header className="brand-site-header">
        <img
          src={import.meta.env.VITE_AFB_LOGO_URL}
          alt="Alliance Franchise Brands"
        />
        <div className="page-banner">
          {isNewSupplier ? (
            <h1 className="company-name">Supplier Registration Form</h1>
          ) : (
            <>
              <div className="company-logo-frame" aria-hidden={!companyLogoUrl}>
                {companyLogoUrl ? (
                  <img src={companyLogoUrl} alt={`${companyName || "Supplier"} logo`} />
                ) : (
                  <i className="fa-solid fa-building" aria-hidden="true" />
                )}
              </div>
              <h1 className="company-name">{companyName || "Supplier"}</h1>
              <button
                type="button"
                className="btn view-profile-btn"
                onClick={handleViewProfile}
              >
                View Profile
                <i className="fa-solid fa-id-card ms-2" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </header>

      <nav aria-label="Form steps" className="wizard-step-tiles">
        {STEPS.map((s) => (
          <button
            key={s.n}
            type="button"
            className={`wizard-step-tile ${step === s.n ? "active" : ""}`}
            onClick={() => setStep(s.n)}
            aria-current={step === s.n ? "step" : undefined}
          >
            <span className="tile-icon">
              <i className={`fa-solid ${s.icon}`} aria-hidden="true" />
            </span>
            <span className="tile-label">{s.n}. {s.label}</span>
          </button>
        ))}
      </nav>

      <section className="card shadow-sm mb-3">
        <div className="card-body p-4">
          {step === 1 && <Step1Business />}
          {step === 2 && <Step2Products />}
          {step === 3 && <Step3Files />}
          {step === 4 && <Step4Contacts />}
          {step === 5 && <Step5Review />}
        </div>
      </section>

      <div className="d-flex justify-content-between">
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={step === 1}
          onClick={() => setStep(step - 1)}
        >
          <i className="fa-solid fa-arrow-left me-1" aria-hidden="true" /> Previous
        </button>
        {step < LAST_STEP ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setStep(step + 1)}
          >
            Next <i className="fa-solid fa-arrow-right ms-1" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                Submitting…
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane me-1" aria-hidden="true" /> Submit
              </>
            )}
          </button>
        )}
      </div>
    </main>
  );
}

/**
 * Branded loading state shown while we wait on /api/supplier/me.
 *
 * Why this exists: prefill can take several seconds on a cold App Service
 * container (B1 SKU has aggressive idle timeouts), and rendering an empty
 * form that suddenly populates is jarring. After ~5 seconds we escalate
 * the message so the user knows we're still alive.
 */
function PrefillLoader() {
  const [longWait, setLongWait] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLongWait(true), 5000);
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      className="container py-5 d-flex flex-column align-items-center justify-content-center text-center"
      style={{ minHeight: "70vh" }}
      role="status"
      aria-live="polite"
    >
      <img
        src={import.meta.env.VITE_AFB_LOGO_URL}
        alt="Alliance Franchise Brands"
        height={56}
        style={{ height: 56, width: "auto" }}
        className="mb-4"
      />
      <div className="spinner-border text-primary mb-3" aria-hidden="true" />
      <h2 className="h5 mb-1">Loading your business profile, contacts, and files…</h2>
      {longWait && (
        <p className="text-muted mb-0">
          This is taking longer than usual — hang tight, we&apos;re almost there.
        </p>
      )}
    </main>
  );
}

function extractErrorDetail(err: unknown): string {
  if (err instanceof AxiosError) {
    if (err.response?.status === 429) {
      return "You've submitted too many times in the last minute. Please wait a moment and try again.";
    }
    const data = err.response?.data as { detail?: unknown } | undefined;
    if (typeof data?.detail === "string") return data.detail;
    if (err.message) return err.message;
  }
  return "Something went wrong. Please try again, and if the problem persists, contact Supplier Relations.";
}
